import { useEffect } from "react";
import { useAdminResourcePersons } from "../../../store/admin";
import {
  formatDate,
  formatCurrency,
  resourcePersonId,
  statusBadgeClass,
} from "./resourcePersonUtils";

/**
 * VIEW — opens a modal that loads the full details for this resource person
 * using the row ID (GET /api/admin/resource-persons/:id).
 */
const ViewResourcePersonModal = ({ isOpen, user, onClose }) => {
  const {
    resourcePersonDetail,
    resourcePersonDetailLoading,
    resourcePersonDetailError,
    fetchResourcePerson,
  } = useAdminResourcePersons();

  // GET /api/admin/resource-persons/:id — triggered with the row's ID.
  useEffect(() => {
    if (isOpen && user) {
      const id = resourcePersonId(user);
      if (id) fetchResourcePerson(id);
    }
  }, [isOpen, user, fetchResourcePerson]);

  if (!isOpen || !user) return null;

  // Prefer freshly fetched detail; fall back to the row data while loading.
  const person = resourcePersonDetail || user;

  const rows = [
    ["Name", person.name || "--"],
    ["Email", person.email || "--"],
    ["Phone", person.phone || "--"],
    ["Bio", person.bio || "--"],
    ["Status", person.status || "--"],
    ["Date Added", formatDate(person.createdAt)],
    ["Last Updated", formatDate(person.updatedAt)],
  ];

  // Stat cards from the detail payload.
  const statCards = [
    { label: "Cumulative Earnings", value: formatCurrency(person.cumulativeEarnings) },
    { label: "Total Courses", value: person.totalCourses ?? 0 },
    { label: "Total Books", value: person.totalBooks ?? 0 },
    { label: "Total Journals", value: person.totalJournals ?? 0 },
    { label: "Total Resources", value: person.totalResources ?? 0 },
    { label: "Total Withdrawals", value: person.totalWithdrawals ?? 0 },
  ];

  return (
    <div
      className="modal"
      style={{
        display: "flex",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1050,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          maxHeight: "90vh",
          overflowY: "auto",
          width: "min(90vw, 620px)",
        }}
      >
        <h5>Resource Person Details</h5>

        {resourcePersonDetailLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : resourcePersonDetailError ? (
          <div className="formErrorDiv">
            <p className="formError">{resourcePersonDetailError}</p>
          </div>
        ) : (
          <>
            {/* PROFILE HEADER */}
            <div className="d-flex align-items-center gap-3 mb-3">
              <img
                src={person.avatar || "/images/default_profile.png"}
                alt="Profile"
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "1px solid #e2e8f0",
                }}
              />
              <div>
                <p className="mb-0 fw-bold">{person.name || "--"}</p>
                <span className="text-muted small">{person.email || ""}</span>
              </div>
            </div>

            {/* STAT CARDS */}
            <div className="row g-2 mb-3">
              {statCards.map((card) => (
                <div className="col-6 col-md-4" key={card.label}>
                  <div
                    className="border rounded p-2 text-center h-100"
                    style={{ borderColor: "#e2e8f0", backgroundColor: "#f8fafc" }}
                  >
                    <p
                      className="m-0 text-uppercase"
                      style={{ fontSize: "10px", fontWeight: 600, color: "#64748b" }}
                    >
                      {card.label}
                    </p>
                    <p className="m-0 fw-bold">{card.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* DETAILS TABLE */}
            <table className="table table-sm mb-3">
              <tbody>
                {rows.map(([label, value]) => (
                  <tr key={label}>
                    <td className="fw-semibold" style={{ width: "140px" }}>
                      {label}
                    </td>
                    <td>
                      {label === "Status" ? (
                        <span className={statusBadgeClass(value)}>{value}</span>
                      ) : (
                        value
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className="modal-buttons">
          <button type="button" className="btn btn-theme2" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewResourcePersonModal;